import React from 'react';
import {BrowserTracing, addIntegration, init, reactRouterV6Instrumentation} from '@sentry/react';
import {createRoutesFromChildren, matchRoutes, useLocation, useNavigationType} from 'react-router-dom';
import {systemHelpers} from '../../utils/helpers';

const isProduction = systemHelpers.isProduction();

if (isProduction) {
  init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENVIRONMENT,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.'
    ],
    integrations: [
      new BrowserTracing({
        // See docs for support of different versions of variation of react router
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
        routingInstrumentation: reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        ),
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^\//],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  // Session Replay is the largest part of Sentry (~124 kB raw) and contributes nothing to error
  // capture or tracing — it records sessions. Adding it after the page has painted keeps that weight
  // off the boot path (H1MVP-4786).
  //
  // Imported from '@sentry/replay' rather than '@sentry/react': the latter re-exports Replay from a
  // barrel, which keeps it statically reachable from the eager import above no matter which names
  // are destructured — the built index.html preloaded it either way. It is pinned to the exact
  // @sentry/react version, because an integration built against a different copy of @sentry/core
  // would register on a different hub and silently never record.
  //
  // `init` itself is deliberately NOT deferred: BrowserTracing's pageload transaction can only be
  // opened while the page is loading, so a late init would stop measuring the thing being measured.
  const addReplay = () => {
    import('@sentry/replay')
      .then(({Replay}) => addIntegration(new Replay()))
      .catch(() => {
        // Best-effort telemetry. A failure here must not surface to the user or disturb the error
        // reporting that is already running.
      });
  };

  if (document.readyState === 'complete') addReplay();
  else window.addEventListener('load', addReplay, {once: true});
}
